import { CloseIcon, LightningIcon } from '@braskai/icons';
import { Button, Modal, Typography } from '@braskai/ui-kit';

import { Box } from '@mui/material';

import { getPaymentErrorContent, useAfterPurchaseModalStore } from '@/entities/afterPurchase';
import { usePurchaseConfirmationModalStore } from '@/entities/purchaseConfirmation';
import { useCurrentUser } from '@/entities/user/currentUser';
import { useReliableUserRefresh } from '@/entities/user/currentUser/model/useReliableUserRefresh';

import { API } from '@/shared/api';
import { FeaturedUpgradeIcon } from '@/shared/assets/icons/FeaturedUpgradeIcon';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Price } from '@/shared/types/Billing';

import { useUpgradeConfirmationModalStore } from '../../model/useUpgradeConfirmationModalStore';
import {
  SActions,
  SBoosterContent,
  SBoosterIncludedCard,
  SCloseButton,
  SContent,
  SHeader,
  SHeaderTop,
  SModalContainer,
  SNewPlanSection,
  SPlanSection,
} from './styled';

const formatDate = (date: Date | string) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const getDowngradeDate = (targetPrice: Price) => {
  const currentDate = new Date();
  const isYearlyPlan = targetPrice.recurring?.interval === PaymentInterval.Year;

  if (isYearlyPlan) {
    currentDate.setFullYear(currentDate.getFullYear() + 1);
  } else {
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return formatDate(currentDate);
};

export const UpgradeConfirmationModal = () => {
  const isOpen = useUpgradeConfirmationModalStore((state) => state.isOpen);
  const close = useUpgradeConfirmationModalStore((state) => state.close);
  const currentPlan = useUpgradeConfirmationModalStore((state) => state.currentPlan);
  const targetPlan = useUpgradeConfirmationModalStore((state) => state.targetPlan);
  const targetPrice = useUpgradeConfirmationModalStore((state) => state.targetPrice);
  const minutesToAdd = useUpgradeConfirmationModalStore((state) => state.minutesToAdd);

  const { reliableRefresh } = useReliableUserRefresh();
  const { data: user } = useCurrentUser();

  const openConfirmation = usePurchaseConfirmationModalStore((state) => state.open);
  const { openError, openSuccess } = useAfterPurchaseModalStore();

  const handleUpgrade = () => {
    if (!targetPrice?.id || !targetPlan?.id) return;

    MixpanelEvents.upgradeNowClick({
      currentPlan: currentPlan?.name ?? '',
      newPlan: targetPlan.name,
    });
    close();

    openConfirmation({
      confirmButtonText: 'Upgrade now',
      currentPlan: currentPlan?.name ?? '',
      newPlan: targetPlan.name,
      promoCodeConfig: {
        enabled: true,
        planId: targetPlan.id,
      },
      fetchPreview: (promoCode) =>
        API.getPlanInvoicePreview({
          price_id: targetPrice.id,
          promotion_code: promoCode ?? undefined,
        }),

      onConfirm: async (invoice, promoCode) => {
        try {
          const res = await API.updatePlan({
            price_id: targetPrice.id,
            promotion_code: promoCode ?? undefined,
          });

          if (!res.paid) {
            const errorContent = getPaymentErrorContent(res?.error_code);
            openError(errorContent.message, res.invoice_url, errorContent.buttonText);
            return;
          }

          if (res.paid) {
            await reliableRefresh();

            const amountDueToday = invoice?.instant_payment?.amount ?? 0;
            const description =
              amountDueToday === 0
                ? 'Your plan has been updated'
                : 'Payment processed successfully, subscription upgraded';
            openSuccess(description, !!amountDueToday);
          }
        } catch (error) {
          console.error('Failed to upgrade plan:', error);
          const errorContent = getPaymentErrorContent();
          openError(errorContent.message, '', errorContent.buttonText);
          throw error;
        }
      },
    });
  };

  const handleCancel = () => {
    close();
  };

  if (!currentPlan || !targetPlan || !targetPrice) {
    return null;
  }

  const currentIntervalLabel = user?.subscription?.plan.interval === PaymentInterval.Year ? 'yearly' : 'monthly';
  const targetIntervalLabel = targetPrice.recurring?.interval === PaymentInterval.Year ? 'yearly' : 'monthly';

  return (
    <Modal open={isOpen} onClose={handleCancel}>
      <SModalContainer>
        {/* Header */}
        <SHeader>
          <SHeaderTop>
            <FeaturedUpgradeIcon height={56} width={56} />
            <SCloseButton onClick={handleCancel}>
              <CloseIcon height={24} width={24} />
            </SCloseButton>
          </SHeaderTop>
        </SHeader>

        {/* Content */}
        <SContent>
          {/* Current Plan */}
          <SPlanSection>
            <Typography color="grey.900" variant="body-1-md">
              Current plan
            </Typography>
            <Typography color="grey.700" variant="body-2">
              You're currently on {currentIntervalLabel} {currentPlan.name}, active until{' '}
              {user?.subscription?.current_period_end && formatDate(user.subscription.current_period_end)}.
            </Typography>
          </SPlanSection>

          {/* New Plan */}
          <SNewPlanSection>
            <SPlanSection>
              <Typography color="primary.600" variant="body-1-md">
                New plan
              </Typography>
              <Typography color="grey.700" variant="body-2">
                You're switching to {targetIntervalLabel} {targetPlan.name}. You'll immediately get new features plus{' '}
                {minutesToAdd || 0} extra minutes (the difference between plans).
              </Typography>
            </SPlanSection>

            {/* Booster Included Card - shown conditionally */}
            {targetPlan.boosters_to_remove && targetPlan.boosters_to_remove.length > 0 && (
              <SBoosterIncludedCard>
                <Box color='primary.500'>
                  <LightningIcon color="primary.500" height={24} width={24} />
                </Box>

                <SBoosterContent>
                  <Typography color="grey.900" variant="body-2-md">
                    Booster included!
                  </Typography>
                  <Typography color="grey.700" variant="body-2">
                    SRT Upload & Download is now included in your plan at no extra cost. We'll remove it from your
                    boosters automatically.
                  </Typography>
                </SBoosterContent>
              </SBoosterIncludedCard>
            )}
          </SNewPlanSection>
        </SContent>

        {/* Actions */}
        <SActions>
          <Button size="lg" fullWidth onClick={handleUpgrade}>
            Upgrade now
          </Button>
          <Typography color="grey.500" sx={{ textAlign: 'center' }} variant="caption-1">
            You can switch back to your previous plan starting {getDowngradeDate(targetPrice)}.
          </Typography>
        </SActions>
      </SModalContainer>
    </Modal>
  );
};
